import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { colors } from "../../../../utils";
import { ASSETS } from "../../../../utils/config";
import TextView from "../../../TextView";
import {
  getBusinessBoxPath,
  getWheelUploadPath,
  handleFileUpload,
} from "../../../../utils/fileUpload";
import { CircularProgress } from "@mui/material";
import { getLoggedInUser, getRandomId } from "../../../../utils/helpers";
import {
  getSelectedSegment,
  getWheelId,
} from "../../../../redux/selectors/segmentBuilderSelectors";
import { useSelector } from "react-redux";
import { saveRecentMedia } from "../../../../services/api";
import FeatureEligibility from "../../../FeatureEligibility";
import { FEATURES } from "../../../../constants/features";
import { useIfUserEligible } from "../../../../hooks/useIfUserEligible";

const allowedMimeTypes = {
  image: ["png", "jpg", "jpeg", "gif", "heic", "svg", "webp", "avif"],
  images: ["png", "jpg", "jpeg", "gif", "heic", "svg", "webp", "avif"],
  video: ["mp4", "mov", "avi", "mkv"],
  documents: ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "csv"],
  document: ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "csv"],
  audio: ["mp3", "wav"],
  audios: ["mp3", "wav"],
};

const Container = styled.div`
  width: 100%;
  background-color: ${colors.background};
  border-radius: 8px;
  border: 1px dashed ${colors.purple};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  height: 140px;
  margin: 10px 0px;
  cursor: pointer;
`;

const BoldText = styled.span`
  font-weight: bold;
  text-decoration: underline;
  cursor: pointer;
`;

const Icon = styled.img`
  margin-bottom: 10px;
  width: 50px;
  height: 50px;
`;

const HiddenInput = styled.input`
  display: none;
`;

const InfoContainer = styled.div`
  margin-top: 5px;
  padding: 5px 10px;
  border-radius: 4px;
  margin-bottom: 5px;
`;

export default function UploadMedia({
  type = "image",
  handleMedia,
  isBusinessBox = false,
  media,
  setMedia,
}) {
  const fileIcons = {
    image: ASSETS.image,
    images: ASSETS.image,
    video: ASSETS.video,
    document: ASSETS.document,
    documents: ASSETS.document,
    audio: ASSETS.mp3Icon,
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const fileInputRef = useRef(null);

  const {
    isEligible,
    subscription,
    isCheckingEligibility,
    requiredSubscription,
  } = useIfUserEligible(type === "video" ? FEATURES.UPLOAD_VIDEOS : null);

  const isMimeTypeAllowed = (file) => {
    const extension = file.name.split(".").pop().toLowerCase();
    return allowedMimeTypes[type].includes(extension);
  };

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleMediaChange = (e) => {
    const files = Array.from(e.target.files);
    if (files && files.length > 0) {
      uploadFiles(files);
      e.target.value = null;
    }
  };

  const wheelId = useSelector(getWheelId);

  const user_id = getLoggedInUser().id;
  const WheelUploadPath = getWheelUploadPath({
    user_id: user_id,
    wheel_id: wheelId,
  });
  const BussinessBoxUploadPath = getBusinessBoxPath({ user_id });
  const uploadPath = isBusinessBox ? BussinessBoxUploadPath : WheelUploadPath;

  const getFileType = () => {
    switch (type) {
      case "images":
        return "0";
      case "image":
        return "0";
      case "video":
        return "1";
      case "documents":
        return "2";
      case "audio":
        return "3";
    }
  };
  const uploadFiles = async (files) => {
    setError(false);
    if (type === "images" || type == "documents" || type == "audio") {
      try {
        setLoading(true);
        const recentFiles = [];
        for (const file of files) {
          const id = getRandomId();
          try {
            if (file.size / 1000000 > 50) {
              setError(true);
              throw `${file.name} cannot be uploaded. File size cannot be greater than 50MB. `;
            }

            handleMedia({ id, loading: true });
            const result = await handleFileUpload(
              file,
              uploadPath,
              wheelId,
              null
            );
            const fileExtension = file.name.split(".").pop();

            const uploadedFile = {
              id: id,
              src: result.location,
              title: file.name,
              loading: false,
              type: fileExtension,
            };
            recentFiles.push({
              type: getFileType(),
              url: result.location,
              metadata: {
                title: file.name,
                type: fileExtension,
              },
            });
            handleMedia(uploadedFile);
          } catch (err) {
            const filteredFiles = media.filter((file) => file.id !== id);
            setMedia(filteredFiles);
            setLoading(false);
            console.log(err);
            toast.error(err);
          }
        }

        setLoading(false);

        // Save each file using saveRecentMedia
        if (recentFiles.length > 0) {
          try {
            await Promise.all(
              recentFiles.map(async (file) => {
                await saveRecentMedia(wheelId, file);
              })
            );
          } catch (apiError) {
            console.log("Failed to save some files.");
          }
        }
      } catch (err) {
        toast.error(err);
        setLoading(false);
      }
    } else {
      const id = getRandomId();
      try {
        setLoading(true);

        handleMedia({ id, loading: true });
        const { location } = await handleFileUpload(
          files[0],
          uploadPath,
          wheelId,
          null
        );
        handleMedia({
          id,
          src: location,
          title: files[0].name,
          loading: false,
        });

        try {
          await saveRecentMedia(wheelId, {
            type: getFileType(),
            url: location,
            metadata: {
              type: files[0].type,
              title: files[0].name,
            },
          });
        } catch (err) {
          console.log(err);
        }

        setLoading(false);
      } catch (err) {
        handleMedia({ id, loading: false });
        toast.error(err);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const handleWindowDrop = (e) => {
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files);
      if (files && files.length > 0) {
        const validFiles = files.filter(isMimeTypeAllowed);
        if (validFiles.length !== files.length) {
          toast.error("Some file types not supported!");
        }
        uploadFiles(validFiles);
      }
    };

    const handleWindowDragOver = (e) => {
      e.preventDefault();
    };

    window.addEventListener("drop", handleWindowDrop);
    window.addEventListener("dragover", handleWindowDragOver);

    return () => {
      window.removeEventListener("drop", handleWindowDrop);
      window.removeEventListener("dragover", handleWindowDragOver);
    };
  }, []);

  const isActive = false;

  const getPlaceholderText = () => {
    switch (type) {
      case "image":
        return "Drag and Drop image here or";
      case "video":
        return "Drag and Drop video here or";
      case "documents":
        return "Drag and Drop document here or";
      case "document":
        return "Drag and Drop document here or";
      case "audio":
        return "Drag and Drop audio file here or";
      default:
        return "Drag and Drop file here or";
    }
  };

  const getAcceptedFileTypes = () => {
    switch (type) {
      case "image":
        return "image/png, image/jpeg, image/jpeg, image/gif, image/heic, image/svg+xml, image/webp, image/avif";
      case "images":
        return "image/png, image/jpeg, image/jpeg, image/gif, image/heic, image/svg+xml, image/webp, image/avif";
      case "video":
        return "video/mp4, video/mov, video/avi, video/mkv, video/quicktime";
      case "documents":
        return "application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation, text/csv";
      case "document":
        return "application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation, text/csv";
      case "audio":
        return "audio/mpeg, audio/wav, audio/ogg, audio/flac, audio/aac";
      case "audios":
        return "audio/mpeg, audio/wav, audio/ogg, audio/flac, audio/aac";
      default:
        return "";
    }
  };

  if (isCheckingEligibility) {
    return (
      <Container>
        <CircularProgress />
      </Container>
    );
  }

  if (!isEligible) {
    return (
      <FeatureEligibility
        subscription={subscription}
        requiredSubscription={requiredSubscription}
      />
    );
  }

  return (
    <>
      <Container
        style={{
          borderColor: isActive ? colors.green : colors.purple,
        }}
        onClick={handleClick}
      >
        <HiddenInput
          type="file"
          ref={fileInputRef}
          onChange={handleMediaChange}
          accept={getAcceptedFileTypes()}
          multiple={
            type === "images" || type === "documents" || type == "audio"
          }
        />

        {loading ? (
          <CircularProgress />
        ) : (
          <>
            <Icon src={fileIcons[type]} />
            <TextView style={{ textAlign: "center" }}>
              {getPlaceholderText()}{" "}
              <BoldText>
                Choose {type.charAt(0).toUpperCase() + type.slice(1)}
              </BoldText>
            </TextView>
          </>
        )}
      </Container>
      <InfoContainer
        style={{
          backgroundColor: colors.lightBorder,
        }}
      >
        <TextView>
          <span style={{ fontWeight: "bold" }}>{"Valid formats: "}</span>{" "}
          {allowedMimeTypes[type].join(", ")}
        </TextView>
      </InfoContainer>

      {error && (
        <InfoContainer
          style={{
            backgroundColor: "#FFEFEF",
          }}
        >
          <TextView>
            <b>Error:</b> Upload exceeds 50MB limit.
          </TextView>
        </InfoContainer>
      )}
    </>
  );
}
